import { useState, useEffect } from "react";
import { formatDateOrTime, updateDate, updateTime } from "../../utils";
import { DatePicker } from "../DatePicker";
import { TimeInput } from "../TimeInput";

interface DateTimeRange {
  start: Date | null;
  end: Date | null;
}

// Helper function to check if a value is a valid range object
const isValidRangeObject = (value: any): value is DateTimeRange => {
  return (
    value &&
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    "start" in value
  );
};

interface DateTimePickerProps {
  value?: string | Date | null | DateTimeRange;
  onChange: (date: Date | null | DateTimeRange) => void;
  className?: string;
  labels?:
    | {
        date?: string;
        time?: string;
        startDate?: string;
        startTime?: string;
        endDate?: string;
        endTime?: string;
      }
    | boolean;
  required?: boolean;
  size?: "sm" | "md" | "lg";
  range?: boolean;
}

interface TimeChangeEvent {
  value: string;
}

/**
 * DateTimePicker component that combines date and time selection
 * Supports both single date/time and range selection modes
 */
export const DateTimePicker = ({
  value,
  onChange,
  className = "",
  labels = false,
  required = false,
  size = "md",
  range = false,
  ...props
}: DateTimePickerProps) => {
  const [dateValue, setDateValue] = useState<Date | null>(null);

  // Range date/time state
  const [rangeValue, setRangeValue] = useState<DateTimeRange>(() => {
    if (!range || !value) return { start: null, end: null };
    try {
      // Check if value is a valid range object
      if (isValidRangeObject(value)) {
        return {
          start: value.start ? new Date(value.start) : null,
          end: value.end ? new Date(value.end) : null,
        };
      }
      return { start: null, end: null };
    } catch (e) {
      console.error("Invalid range value:", value);
      return { start: null, end: null };
    }
  });

  useEffect(() => {
    if (range) {
      // Handle range mode
      if (isValidRangeObject(value)) {
        setRangeValue({
          start: value.start ? new Date(value.start) : null,
          end: value.end ? new Date(value.end) : null,
        });
      } else {
        setRangeValue({ start: null, end: null });
      }
    } else {
      // Handle single date mode
      if (value && (typeof value === "string" || value instanceof Date)) {
        try {
          const date = new Date(value as string | Date);
          if (!isNaN(date.getTime())) {
            setDateValue(date);
          } else {
            setDateValue(null);
          }
        } catch (e) {
          console.error("Invalid date value in useEffect:", value);
          setDateValue(null);
        }
      } else {
        setDateValue(null);
      }
    }
  }, [value, range]);

  // Single date/time handlers
  const handleDateChange = ({ value: newDate }: { value: Date | null }) => {
    try {
      if (!newDate) {
        onChange(null);
        setDateValue(null);
        return;
      }
      const updatedDateTime = updateDate(dateValue, newDate);

      if (updatedDateTime && !isNaN(updatedDateTime.getTime())) {
        onChange(updatedDateTime);
      }
    } catch (e) {
      console.error("Error in handleDateChange:", e);
    }
  };

  const handleTimeChange = (e: TimeChangeEvent) => {
    try {
      if (!dateValue) {
        return;
      }
      const newTime = e.value;
      const updatedDateTime = updateTime(dateValue, newTime);

      if (updatedDateTime && !isNaN(updatedDateTime.getTime())) {
        onChange(updatedDateTime);
      }
    } catch (e) {
      console.error("Error in handleTimeChange:", e);
    }
  };

  // Range handlers
  const handleRangeChange = (
    type: "start" | "end",
    field: "date" | "time",
    newValue: Date | string | null
  ) => {
    try {
      const currentRange = { ...rangeValue };

      if (field === "date") {
        if (!newValue) {
          currentRange[type] = null;
        } else {
          const updatedDateTime = updateDate(
            currentRange[type],
            newValue as Date
          );
          if (updatedDateTime && !isNaN(updatedDateTime.getTime())) {
            currentRange[type] = updatedDateTime;
          }
        }
      } else if (field === "time") {
        if (!currentRange[type]) return;
        const updatedDateTime = updateTime(
          currentRange[type],
          newValue as string
        );
        if (updatedDateTime && !isNaN(updatedDateTime.getTime())) {
          currentRange[type] = updatedDateTime;
        }
      }

      // Validate that start is before end
      if (
        currentRange.start &&
        currentRange.end &&
        currentRange.start > currentRange.end
      ) {
        // If start is after end, swap them
        const temp = currentRange.start;
        currentRange.start = currentRange.end;
        currentRange.end = temp;
      }

      setRangeValue(currentRange);
      onChange(currentRange);
    } catch (e) {
      console.error("Error in handleRangeChange:", e);
    }
  };

  // Format values for display
  const formattedDate = (() => {
    try {
      return formatDateOrTime(dateValue, "date");
    } catch (e) {
      console.error("Error formatting date:", e);
      return "";
    }
  })();

  const formattedTime = (() => {
    try {
      return formatDateOrTime(dateValue, "time");
    } catch (e) {
      console.error("Error formatting time:", e);
      return "";
    }
  })();

  const formattedStartDate = (() => {
    try {
      return formatDateOrTime(rangeValue.start, "date");
    } catch (e) {
      console.error("Error formatting start date:", e);
      return "";
    }
  })();

  const formattedStartTime = (() => {
    try {
      return formatDateOrTime(rangeValue.start, "time");
    } catch (e) {
      console.error("Error formatting start time:", e);
      return "";
    }
  })();

  const formattedEndDate = (() => {
    try {
      return formatDateOrTime(rangeValue.end, "date");
    } catch (e) {
      console.error("Error formatting end date:", e);
      return "";
    }
  })();

  const formattedEndTime = (() => {
    try {
      return formatDateOrTime(rangeValue.end, "time");
    } catch (e) {
      console.error("Error formatting end time:", e);
      return "";
    }
  })();

  // Label configuration
  const getLabelConfig = () => {
    const defaultLabels = {
      date: "Appointment date",
      time: "Appointment time",
      startDate: "Start date",
      startTime: "Start time",
      endDate: "End date",
      endTime: "End time",
    };

    if (typeof labels === "object") {
      return { ...defaultLabels, ...labels };
    }
    return defaultLabels;
  };

  const labelConfig = getLabelConfig();

  if (range) {
    return (
      <div className={`space-y-3 ${className}`}>
        {/* Start Date/Time */}
        <div className="flex gap-2 items-center">
          <div className="flex-1">
            <DatePicker
              label={labelConfig.startDate}
              required={required}
              placeholder="Select start date"
              value={formattedStartDate}
              onChange={({ value: newDate }) =>
                handleRangeChange("start", "date", newDate)
              }
              size={size || "md"}
            />
          </div>
          <div className="flex-1">
            <TimeInput
              label={labelConfig.startTime}
              required={required}
              placeholder="Select start time"
              value={formattedStartTime}
              onChange={(e) => handleRangeChange("start", "time", e.value)}
              disabled={!rangeValue.start}
              size={size || "md"}
            />
          </div>
        </div>

        {/* End Date/Time */}
        <div className="flex gap-2 items-center">
          <div className="flex-1">
            <DatePicker
              label={labelConfig.endDate}
              required={required}
              placeholder="Select end date"
              value={formattedEndDate}
              onChange={({ value: newDate }) =>
                handleRangeChange("end", "date", newDate)
              }
              size={size || "md"}
            />
          </div>
          <div className="flex-1">
            <TimeInput
              label={labelConfig.endTime}
              required={required}
              placeholder="Select end time"
              value={formattedEndTime}
              onChange={(e) => handleRangeChange("end", "time", e.value)}
              disabled={!rangeValue.end}
              size={size || "md"}
            />
          </div>
        </div>
      </div>
    );
  }

  // Single date/time mode (original behavior)
  return (
    <div className={`flex gap-2 items-center ${className}`}>
      <div className="flex-1">
        <DatePicker
          label={labelConfig.date}
          required={required}
          placeholder="Select a date"
          value={formattedDate}
          onChange={handleDateChange}
          size={size || "md"}
        />
      </div>
      <div className="flex-1">
        <TimeInput
          label={labelConfig.time}
          required={required}
          placeholder="Select a time"
          value={formattedTime}
          onChange={handleTimeChange}
          disabled={!dateValue}
          size={size || "md"}
        />
      </div>
    </div>
  );
};

// Default export
export default DateTimePicker;
